import React, { useEffect, useRef, useState } from "react";
import { Card, Form, Button } from 'react-bootstrap';
import SecondaryButton from '../../../components/common/SecondaryButton';
import PrimaryButton from '../../../components/common/PrimaryButton';
import Editor from '@monaco-editor/react';
import AutomationService from "../../../services/AutomationService";

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const MIN_IMAGE_SIZE_BYTES = 1024;
const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || process.env.REACT_APP_API_BASE_URL || '';

const PLACEHOLDERS = [
    '{LeadName}', '{LeadEmail}', '{LeadPhone}',
    '{FollowUpDate}', '{StatusName}', '{CompanyName}',
    '{UserName}', '{OtpCode}', '{ExpiryMinutes}'
];

const resolveTemplateImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (!BASE_URL) return url;
    if (url.startsWith('/')) return `${BASE_URL}${url}`;
    return `${BASE_URL}/${url}`;
};

const getResolvedTemplateImage = (imageUrl, templateImage) =>
    resolveTemplateImageUrl(imageUrl || templateImage || null);

// ============================================================
// ✅ Inline Email Template Editor (Monaco + iframe preview)
// ============================================================
const EmailTemplateEditor = ({ value, onChange, imageUrl, savedImageUrl }) => {
    const [activeTab, setActiveTab] = useState('preview'); // ✅ default to preview for non-technical users
    const [htmlContent, setHtmlContent] = useState(value || '');
    const iframeRef = useRef(null);
    const editorRef = useRef(null);
    const editableRef = useRef(null);
    const isSyncingRef = useRef(false);

    // Sync when parent loads a template
    useEffect(() => {
        if (value !== htmlContent) {
            setHtmlContent(value || '');
        }
    }, [value]);

    // ✅ When real saved URL arrives — replace img src in HTML
    useEffect(() => {
        if (!savedImageUrl) return;
        if (savedImageUrl.startsWith('blob:')) return;

        setHtmlContent(prev => {
            const updated = prev.replace(
                /<img([^>]*)src=["'][^"']*["']/i,
                `<img$1src="${savedImageUrl}"`
            );
            onChange(updated);
            return updated;
        });
    }, [savedImageUrl]);

    // ✅ Update iframe (code/split view)
    useEffect(() => {
        if (activeTab === 'preview') return; // preview uses contentEditable, not iframe
        if (!iframeRef.current) return;
        const doc = iframeRef.current.contentDocument
            || iframeRef.current.contentWindow?.document;
        if (!doc) return;
        doc.open();
        doc.write(htmlContent);
        doc.close();
    }, [htmlContent, activeTab]);

    // ✅ Sync contentEditable when switching TO preview tab
    useEffect(() => {
        if (activeTab !== 'preview') return;
        if (!editableRef.current) return;

        // Extract just the body content for editing
        const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        const bodyContent = bodyMatch ? bodyMatch[1] : htmlContent;
        editableRef.current.innerHTML = bodyContent;
    }, [activeTab, htmlContent]);

    // ✅ When user edits in preview — sync back to full HTML
    const handleEditableInput = () => {
        if (!editableRef.current || isSyncingRef.current) return;
        const editedBody = editableRef.current.innerHTML;

        // Re-inject edited body back into full HTML structure
        const updated = htmlContent.replace(
            /(<body[^>]*>)([\s\S]*)(<\/body>)/i,
            `$1${editedBody}$3`
        );
        setHtmlContent(updated);
        onChange(updated);
    };

    const handleEditorChange = (val) => {
        const updated = val || '';
        setHtmlContent(updated);
        onChange(updated);
    };

    const insertPlaceholder = (placeholder) => {
        if (activeTab === 'preview' && editableRef.current) {
            // ✅ Insert placeholder at cursor in contentEditable
            editableRef.current.focus();
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
                const range = sel.getRangeAt(0);
                range.deleteContents();
                const textNode = document.createTextNode(placeholder);
                range.insertNode(textNode);
                range.setStartAfter(textNode);
                range.collapse(true);
                sel.removeAllRanges();
                sel.addRange(range);
                handleEditableInput();
            }
            return;
        }

        if (editorRef.current) {
            const editor = editorRef.current;
            const selection = editor.getSelection();
            const id = { major: 1, minor: 1 };
            const op = {
                identifier: id,
                range: selection,
                text: placeholder,
                forceMoveMarkers: true
            };
            editor.executeEdits('insert-placeholder', [op]);
            editor.focus();
        }
    };

    const tabBtn = (tab, label) => (
        <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
                background: activeTab === tab ? '#4a90e2' : '#f0f4ff',
                color: activeTab === tab ? '#fff' : '#4a90e2',
                border: '1px solid #4a90e2',
                borderRadius: 6,
                padding: '4px 14px',
                fontSize: 12,
                cursor: 'pointer',
                marginRight: 6
            }}
        >
            {label}
        </button>
    );

    return (
        <div>
            {/* Toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">                
                <div className="d-flex align-items-center gap-1 flex-wrap">
                    <span className="small text-muted me-1">Insert:</span>
                    {PLACEHOLDERS.map(p => (
                        <button
                            key={p}
                            onClick={() => insertPlaceholder(p)}
                            title={`Insert ${p}`}
                            style={{
                                fontSize: 11, padding: '2px 8px',
                                borderRadius: 12, border: '1px solid #ccc',
                                background: '#f7f7f9', cursor: 'pointer'
                            }}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            </div>

            <div style={{
                display: 'grid',
                gridTemplateColumns:
                    activeTab === 'split' ? '1fr 1fr' : '1fr',
                border: '1px solid #dee2e6',
                borderRadius: 8,
                overflow: 'hidden',
                minHeight: 500
            }}>
                {/* ✅ Visual editor pane (contentEditable) */}
                {(activeTab === 'preview') && (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{
                            background: '#e8f5e9', padding: '4px 12px',
                            fontSize: 11, color: '#2e7d32',
                            borderBottom: '1px solid #dee2e6',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <span>✏️ Click anywhere to edit directly</span>
                            <span style={{ fontSize: 10, color: '#888' }}>
                                Text, links and placeholders are editable
                            </span>
                        </div>
                        <div
                            ref={editableRef}
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={handleEditableInput}
                            style={{
                                flex: 1,
                                minHeight: 470,
                                padding: 16,
                                outline: 'none',
                                overflowY: 'auto',
                                background: '#fff',
                                fontFamily: 'Arial, sans-serif',
                                fontSize: 14,
                                lineHeight: 1.6,
                                cursor: 'text'
                            }}
                        />
                    </div>
                )}

                {/* HTML code pane */}
                {(activeTab === 'code' || activeTab === 'split') && (
                    <div style={{
                        borderRight: activeTab === 'split' ? '1px solid #dee2e6' : 'none',
                        display: 'flex', flexDirection: 'column'
                    }}>
                        <div style={{
                            background: '#f0f4ff', padding: '4px 12px',
                            fontSize: 11, color: '#666',
                            borderBottom: '1px solid #dee2e6'
                        }}>
                            HTML Editor
                        </div>
                        <Editor
                            height="470px"
                            language="html"
                            value={htmlContent}
                            onChange={handleEditorChange}
                            onMount={(editor) => { editorRef.current = editor; }}
                            options={{
                                minimap: { enabled: false },
                                fontSize: 13,
                                wordWrap: 'on',
                                lineNumbers: 'on',
                                scrollBeyondLastLine: false,
                                automaticLayout: true,
                                tabSize: 2,
                            }}
                        />
                    </div>
                )}

                {/* iframe preview pane (split mode only) */}
                {activeTab === 'split' && (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{
                            background: '#f0f4ff', padding: '4px 12px',
                            fontSize: 11, color: '#666',
                            borderBottom: '1px solid #dee2e6'
                        }}>
                            Live Preview
                        </div>
                        <iframe
                            ref={iframeRef}
                            title="Email Preview"
                            style={{ width: '100%', height: '470px', border: 'none', background: '#fff' }}
                            sandbox="allow-same-origin"
                        />
                    </div>
                )}
            </div>

            <div className="mt-2 small text-muted">
                💡 Use <strong>Visual Editor</strong> to edit like a document · <strong>Split</strong> to see code and preview side by side · <strong>HTML</strong> for full code control
            </div>
        </div>
    );
};

// ============================================================
// ✅ Main FollowUpTemplate component
// ============================================================
const FollowUpTemplate = () => {

    const fileInputRef = useRef(null);

    const [templates, setTemplates] = useState([]);
    const [name, setName] = useState('');
    const [content, setContent] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [defaultSelectedEmailTemplateId, setDefaultSelectedEmailTemplateId] = useState(null);
    const [showChooser, setShowChooser] = useState(false);
    const [defaultTemplates, setDefaultTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const [templateImage, setTemplateImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [existingImageUrl, setExistingImageUrl] = useState(null);
    const [imageError, setImageError] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    const fetchTemplates = async () => {
        try {
            const response = await AutomationService.fetchAllEmailFollowUpTemplates();
            if (response.data?.success && response.data.data) {
                const fetchedTemplates = response.data.data.map(template => {
                    const resolvedImageUrl = resolveTemplateImageUrl(template.imageUrl || template.templateImage || null);
                    return {
                        id: template.id,
                        title: template.name,
                        emailFollowUpCategoryId: template.emailFollowUpCategoryId,
                        createdAt: new Date(template.createdOn).toLocaleDateString('en-US', {
                            year: 'numeric', month: 'long', day: 'numeric'
                        }),
                        content: template.templateBody,
                        imageUrl: resolvedImageUrl,
                        templateImage: template.templateImage ?? null
                    };
                });
                setTemplates(fetchedTemplates);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    };

    useEffect(() => { fetchTemplates(); }, []);

    useEffect(() => {
        return () => { if (imagePreview) URL.revokeObjectURL(imagePreview); };
    }, [imagePreview]);

    const validateAndSetImage = (file) => {
        setImageError('');
        if (!file) return;
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            setImageError('Invalid file type. Only JPEG, PNG, GIF and WebP are allowed.');
            return false;
        }
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            setImageError('File size exceeds the 5MB limit.');
            return false;
        }
        if (file.size < MIN_IMAGE_SIZE_BYTES) {
            setImageError('File is too small or may be corrupt.');
            return false;
        }
        setTemplateImage(file);
        setImagePreview(URL.createObjectURL(file));
        setExistingImageUrl(null);
        return true;
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Client-side validation first
        setImageError('');
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            setImageError('Invalid file type. Only JPEG, PNG, GIF and WebP are allowed.');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            setImageError('File size exceeds the 5MB limit.');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
        if (file.size < MIN_IMAGE_SIZE_BYTES) {
            setImageError('File is too small or may be corrupt.');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        // ✅ Show local blob preview immediately while uploading
        setImagePreview(URL.createObjectURL(file));
        setIsUploadingImage(true);

        try {
            const formData = new FormData();
            formData.append('image', file);

            const response = await AutomationService.UploadEmailTemplateImage(formData);

            if (response.data?.success) {
                const realUrl = response.data.data; // ✅ real server URL
                setExistingImageUrl(realUrl);        // ✅ triggers editor to replace img src
                setTemplateImage(null);              // no need to send file again on save
                setImagePreview(null);               // drop blob preview, real URL takes over
            } else {
                setImageError('Image upload failed. Please try again.');
                setImagePreview(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
        } catch (error) {
            console.error('Image upload error:', error);
            setImageError('Image upload failed. Please try again.');
            setImagePreview(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } finally {
            setIsUploadingImage(false);
        }
    };

    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);
    const handleDrop = (e) => {
        e.preventDefault(); setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (!validateAndSetImage(file) && fileInputRef.current)
            fileInputRef.current.value = '';
    };

    const handleRemoveImage = () => {
        setTemplateImage(null); setImagePreview(null);
        setExistingImageUrl(null); setImageError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const resetImageState = () => {
        setTemplateImage(null); setImagePreview(null);
        setExistingImageUrl(null); setImageError('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDelete = (id) => setTemplates(prev => prev.filter(t => t.id !== id));

    const handleSelect = (t) => {
        setSelectedEmailTemplateId(t.id);
        setName(t.title);
        setContent(t.content);
        setDefaultSelectedEmailTemplateId(t.emailFollowUpCategoryId);
        setExistingImageUrl(getResolvedTemplateImage(t.imageUrl, t.templateImage));
        setTemplateImage(null); setImagePreview(null); setImageError('');
        setShowForm(true);
    };

    const handleAddClick = async () => {
        setLoadingTemplates(true);
        try {
            const response = await AutomationService.fetchAllDefaultEmailFollowUpTemplates();
            if (response.data?.success) {
                setDefaultTemplates(response.data.data.map(t => ({
                    id: t.id, title: t.name, content: t.templateBody
                })));
            }
        } catch (error) {
            console.error('Error fetching default templates:', error);
        } finally {
            setLoadingTemplates(false);
            setShowChooser(true);
        }
    };

    const handleCancel = () => {
        setName(''); setContent('');
        setDefaultSelectedEmailTemplateId(null);
        setSelectedEmailTemplateId(null);
        resetImageState();
        setShowForm(templates.length === 0);
    };

    const handleSelectTemplate = (template) => {
        setName(template.title);
        setContent(template.content);
        setDefaultSelectedEmailTemplateId(template.id);
        resetImageState();
        setShowForm(true);
        setShowChooser(false);
    };

    const buildFormData = (isUpdate = false) => {
        const formData = new FormData();
        if (isUpdate) formData.append('id', selectedEmailTemplateId);
        formData.append('emailFollowUpCategoryId',
            defaultSelectedEmailTemplateId || (defaultTemplates.length > 0 ? defaultTemplates[0].id : null));
        formData.append('name', name);
        formData.append('templateBody', content);        
        return formData;
    };

    const handleCreateTemplate = async () => {
        if (!name || !content) { alert('Please provide both a name and content.'); return; }
        if (imageError) return;
        setIsSaving(true);
        try {
            const response = await AutomationService.CreateEmailFollowUpTemplate(buildFormData(false));
            if (response.data?.success) {
                alert('Template created successfully!');
                setName(''); setContent(''); resetImageState(); setShowForm(false);
            } else {
                alert('Failed to create template: ' + response.message);
            }
        } catch (error) {
            console.error('Error creating template:', error);
            alert('An error occurred while creating the template.');
        } finally {
            setIsSaving(false);
            fetchTemplates();
        }
    };

    const handleUpdateTemplate = async () => {
        if (!name || !content) { alert('Please provide both a name and content.'); return; }
        if (imageError) return;
        setIsSaving(true);
        try {
            const response = await AutomationService.UpdateEmailFollowUpTemplate(buildFormData(true));
            if (response.data?.success) {
                alert('Template updated successfully!');
                setName(''); setContent('');
                setSelectedEmailTemplateId(null);
                setDefaultSelectedEmailTemplateId(null);
                resetImageState(); setShowForm(false);
            } else {
                alert('Failed to update template: ' + response.message);
            }
        } catch (error) {
            console.error('Error updating template:', error);
            alert('An error occurred while updating the template.');
        } finally {
            setIsSaving(false);
            fetchTemplates();
        }
    };

    const handleAddEditEmailTemplate = () =>
        selectedEmailTemplateId ? handleUpdateTemplate() : handleCreateTemplate();

    const createCardStyle = {
        backgroundColor: '#f3f8ff', borderRadius: 10,
        padding: 24, border: '1px solid #e3eeff'
    };
    const templateContentStyle = {
        backgroundColor: '#f7f7f9', padding: '12px 16px', borderRadius: 6
    };
    const dropZoneStyle = {
        border: `2px dashed ${isDragging ? '#4a90e2' : imagePreview || existingImageUrl ? '#4CAF50' : '#c5d5f0'}`,
        borderRadius: 8,
        padding: '20px 16px',
        backgroundColor: isDragging ? '#eaf3ff' : imagePreview || existingImageUrl ? '#f0fff4' : '#fafcff',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    };

    // Active image url to pass to editor
    const activeImageUrl = imagePreview || existingImageUrl || null;

    return (
        <div>
            {/* Header */}
            <div className="mb-4 d-flex justify-content-between align-items-start">
                <div>
                    <h3 className="fw-bold mb-1">Communication Templates</h3>
                    <p className="text-muted">Create reusable templates for email and WhatsApp messages</p>
                </div>
                <div>
                    {!showForm && !showChooser &&
                        <Button variant="primary" onClick={handleAddClick}>Add Template</Button>}
                </div>
            </div>

            {/* Template chooser */}
            {showChooser ? (
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h5 className="mb-1 fw-bold">Select a Default Template</h5>
                            <p className="text-muted mb-0 small">Choose from pre-built templates and customize as needed</p>
                        </div>
                        <SecondaryButton onClick={() => setShowChooser(false)}>Cancel</SecondaryButton>
                    </div>
                    {loadingTemplates ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary mb-3" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-muted">Loading templates...</p>
                        </div>
                    ) : defaultTemplates.length > 0 ? (
                        <div className="row g-3">
                            {defaultTemplates.map(t => (
                                <div className="col-md-6 col-lg-4" key={t.id}>
                                    <Card className="h-100 border-0 shadow-sm" style={{
                                        borderRadius: 8, overflow: 'hidden', transition: 'all 0.3s ease',
                                        cursor: 'pointer', padding: 16, margin: 8,
                                        borderWidth: 1.5, borderStyle: 'solid', borderColor: '#e3eeff',
                                        boxShadow: '0 6px 8px rgba(0,0,0,0.1)'
                                    }}
                                        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                    >
                                        <div style={{ background: 'linear-gradient(135deg, #D07C3F 0%, #E8A366 100%)', height: 60, marginBottom: 12 }} />
                                        <Card.Body className="d-flex flex-column" style={{ minHeight: 220, padding: 12 }}>
                                            <h6 className="fw-bold mb-2 text-dark" style={{ fontSize: '1rem' }}>{t.title}</h6>
                                            <div className="text-muted small flex-grow-1 mb-3"
                                                style={{ lineHeight: 1.5, padding: 8 }}
                                                dangerouslySetInnerHTML={{ __html: t.content }} />
                                            <PrimaryButton size="sm" className="w-100"
                                                onClick={() => handleSelectTemplate(t)}
                                                style={{ borderRadius: 8, marginTop: 12 }}>
                                                <i className="bi bi-check-circle me-2" />Use Template
                                            </PrimaryButton>
                                        </Card.Body>
                                    </Card>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <p className="text-muted">No templates available</p>
                        </div>
                    )}
                </div>

            ) : showForm ? (
                <div style={createCardStyle} className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0 fw-bold">
                            {selectedEmailTemplateId ? 'Edit Template' : 'Create New Template'}
                        </h5>
                        {templates.length > 0 &&
                            <SecondaryButton onClick={() => { setShowForm(false); resetImageState(); }}>
                                Back to list
                            </SecondaryButton>}
                    </div>

                    <Form>
                        {/* Template name */}
                        <Form.Group className="mb-3" controlId="templateName">
                            <Form.Label className="small fw-semibold text-muted">Template Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., Welcome Email, Follow-up Message"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                style={{ borderRadius: 6 }}
                            />
                        </Form.Group>

                        {/* ✅ Monaco Editor + iframe preview */}
                        <Form.Group className="mb-3" controlId="templateContent">
                            <Form.Label className="small fw-semibold text-muted">Template Content</Form.Label>
                            <EmailTemplateEditor
                                value={content}
                                onChange={setContent}
                                imageUrl={imagePreview || existingImageUrl}
                                savedImageUrl={existingImageUrl} // ✅ only real URLs, never blob
                            />
                        </Form.Group>

                        {/* Header / Banner image upload */}
                        <Form.Group className="mb-3" controlId="templateImage">
                            <Form.Label className="small fw-semibold text-muted">
                                Header / Banner Image
                                <span className="fw-normal ms-1">(optional · JPEG, PNG, GIF, WebP · max 5MB)</span>
                            </Form.Label>

                            <div
                                style={dropZoneStyle}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {isUploadingImage ? (
                                        <div onClick={e => e.stopPropagation()}>
                                            {imagePreview && (
                                                <img src={imagePreview} alt="Uploading..."
                                                    style={{ maxHeight: 80, maxWidth: '100%', borderRadius: 6, marginBottom: 10, opacity: 0.5 }} />
                                            )}
                                            <div className="spinner-border text-primary mb-2" role="status">
                                                <span className="visually-hidden">Uploading...</span>
                                            </div>
                                            <p className="text-muted small mb-0">Uploading image to server...</p>
                                        </div>
                                    ) : existingImageUrl ? (
                                    <div onClick={e => e.stopPropagation()}>
                                        <img src={existingImageUrl} alt="Current banner"
                                            style={{ maxHeight: 120, maxWidth: '100%', borderRadius: 6, marginBottom: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }} />
                                        <div>
                                            <span className="badge bg-primary me-2">Current image</span>
                                            <Button variant="outline-secondary" size="sm"
                                                onClick={() => fileInputRef.current?.click()}
                                                style={{ borderRadius: 20, fontSize: 12, marginRight: 8 }}>🔄 Replace</Button>
                                            <Button variant="outline-danger" size="sm" onClick={handleRemoveImage}
                                                style={{ borderRadius: 20, fontSize: 12 }}>✕ Remove</Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{ fontSize: 32, marginBottom: 8 }}>🖼️</div>
                                        <p className="mb-1 fw-semibold text-muted" style={{ fontSize: 14 }}>
                                            Drag & drop an image here, or <span style={{ color: '#4a90e2', textDecoration: 'underline' }}>browse</span>
                                        </p>
                                        <p className="text-muted mb-0" style={{ fontSize: 12 }}>JPEG, PNG, GIF, WebP · Max 5MB</p>
                                    </div>
                                )}
                            </div>

                            <input ref={fileInputRef} type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                onChange={handleImageChange} style={{ display: 'none' }} />

                            {imageError && <div className="mt-2 text-danger small">⚠️ {imageError}</div>}
                        </Form.Group>

                        {/* Placeholder tip */}
                        <div className="mb-3 p-3 small text-muted"
                            style={{ background: '#fffbe6', borderRadius: 6, border: '1px solid #ffe58f' }}>
                            💡 <strong>Tip:</strong> Placeholders like <code>{'{LeadName}'}</code>, <code>{'{LeadEmail}'}</code>,{' '}
                            <code>{'{FollowUpDate}'}</code>, <code>{'{StatusName}'}</code> will be dynamically replaced when the email is sent.
                        </div>

                        {/* Actions */}
                        <div className="d-flex gap-2">
                            <Button variant="primary" onClick={handleAddEditEmailTemplate}
                                disabled={!!imageError || isSaving} style={{ borderRadius: 6, minWidth: 130 }}>
                                {isSaving
                                    ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Saving...</>
                                    : selectedEmailTemplateId ? 'Save Changes' : 'Save Template'}
                            </Button>
                            <Button variant="light" onClick={handleCancel} style={{ borderRadius: 6 }}>Cancel</Button>
                        </div>
                    </Form>
                </div>

            ) : (
                /* Template list */
                <div>
                    {templates.map(t => (
                        <Card className="mb-3" key={t.id} style={{ borderRadius: 8, border: '1px solid #e3eeff' }}>
                            {t.imageUrl && (
                                <img src={t.imageUrl} alt={t.title}
                                    style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: '8px 8px 0 0' }} />
                            )}
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div>
                                        <div className="fw-semibold">{t.title}</div>
                                        <div className="small text-muted">Created {t.createdAt}</div>
                                    </div>
                                    <div>
                                        <Button variant="link" className="text-primary p-0 me-2"
                                            title="Edit" onClick={() => handleSelect(t)}>✏️</Button>
                                        <Button variant="link" className="text-danger p-0" title="Delete"
                                            onClick={e => { e.stopPropagation(); handleDelete(t.id); }}>🗑️</Button>
                                    </div>
                                </div>
                                {/* ✅ List view shows iframe mini-preview of full HTML */}
                                <iframe
                                    title={t.title}
                                    srcDoc={t.content}
                                    sandbox="allow-same-origin"
                                    style={{
                                        width: '100%', height: 180,
                                        border: '1px solid #e3eeff',
                                        borderRadius: 6, pointerEvents: 'none'
                                    }}
                                />
                                <Button variant="link" className="p-0 mt-2 small"
                                    onClick={() => handleSelect(t)}>
                                    ✏️ Edit this template
                                </Button>
                            </Card.Body>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FollowUpTemplate;